import { defineStore } from 'pinia'
import { Invoice } from '@team-milva/lab-models/invoice'
import type { IInvoice } from '@team-milva/lab-models/invoice'
import { collection, onSnapshot, query, where } from 'firebase/firestore'
import { db } from '@/firebase/init'
import { useAccountStore } from '@/stores/accountStore'
import { invoiceSyncSeverity } from '@/globals/typescript/utils/orderInvoices'

export type TInvoicesPaymentStatusFilter =
  | 'all'
  | 'ready-to-charge'
  | 'invoice-sent'
  | 'failed'
  | 'posted-not-sent'

export const useInvoiceStore = defineStore('invoice', {
  state: () => ({
    allInvoices: [] as Invoice[],
    unsubscribeAllInvoices: null as (() => void) | null,
    // The invoices list's payment-status chips — in the store so the selection survives
    // navigating into an invoice and back.
    paymentStatusFilter: 'all' as TInvoicesPaymentStatusFilter,
  }),

  getters: {
    allInvoicesLoaded: (state): boolean => !!state.unsubscribeAllInvoices,

    invoicesByOrderID: (state): Record<string, Invoice> => {
      const map: Record<string, Invoice> = {}
      for (const invoice of state.allInvoices) {
        for (const orderID of invoice.orderIDs) {
          const existing = map[orderID]
          if (!existing) {
            map[orderID] = invoice
            continue
          }
          // Since credit notes, an order can be covered by more than one Invoice document over
          // its life: a fully credited invoice keeps its `orderIDs` as the record of what it
          // covered, while the order itself is detached and re-invoiced onto a new document.
          // Without this, which of the two won came down to their order in `allInvoices`.
          const existingIsCredited = existing.credit?.status === 'completed'
          const candidateIsCredited = invoice.credit?.status === 'completed'
          if (existingIsCredited && !candidateIsCredited) {
            map[orderID] = invoice
            continue
          }
          // Two live invoices on one order: a split order has one per side. The order-level chip
          // reads this one, so it is the side needing the most attention (failed before
          // posted-not-sent before in progress) — never a healthy side hiding a failed one.
          if (!existingIsCredited && !candidateIsCredited && invoiceSyncSeverity(invoice) > invoiceSyncSeverity(existing)) {
            map[orderID] = invoice
          }
        }
      }
      return map
    },

    /** Every invoice that covers an order, credited ones included — a split order has two live
     *  invoices (one per side) and, over its life, credited ones besides. */
    invoicesListByOrderID: (state): Record<string, Invoice[]> => {
      const map: Record<string, Invoice[]> = {}
      for (const invoice of state.allInvoices) {
        for (const orderID of invoice.orderIDs) {
          ;(map[orderID] ??= []).push(invoice)
        }
      }
      return map
    },
  },

  actions: {
    getInvoices(): Promise<Invoice[]> {
      if (this.allInvoicesLoaded) {
        return Promise.resolve(this.allInvoices)
      }

      return new Promise((resolve) => {
        let resolved = false

        const accountStore = useAccountStore()

        const q = query(
          collection(db, 'Apps', '-lab-customer-', 'Invoices'),
          where('labAccountID', '==', accountStore.labAccount?.id)
        )

        const unsubscribe = onSnapshot(q, (querySnapshot) => {
          this.allInvoices = querySnapshot.docs.map(
            (docSnap) => new Invoice(docSnap.data() as IInvoice)
          )

          if (!resolved) {
            resolved = true
            resolve(this.allInvoices)
          }
        })

        this.unsubscribeAllInvoices = unsubscribe
      })
    },

    resetStore(): void {
      this.allInvoices = []
      this.paymentStatusFilter = 'all'

      if (this.unsubscribeAllInvoices) {
        this.unsubscribeAllInvoices()
        this.unsubscribeAllInvoices = null
      }
    },
  },
})
